import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PropTypes from 'prop-types';

const AccessDeniedPage = ({ requiredRoles, redirectPath }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Auto-redirect after 3 seconds
    const timer = setTimeout(() => {
      if (redirectPath) {
        navigate(redirectPath);
      } else {
        // Default redirect based on user role
        switch (user?.role) {
          case 'student':
            navigate('/students/dashboard');
            break;
          case 'teacher':
            navigate('/teachers/dashboard');
            break;
          case 'admin':
            navigate('/admin/dashboard');
            break;
          default:
            navigate('/login');
        }
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [navigate, user, redirectPath]);

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'teacher': return 'Teacher';
      case 'student': return 'Student';
      default: return role;
    }
  };

  const getRecommendedAction = () => {
    switch (user?.role) {
      case 'student':
        return {
          message: "As a student, you can view your quiz results and performance in your Records section.",
          actionText: "Go to My Records",
          actionPath: "/students/dashboard/records"
        };
      case 'teacher':
        return {
          message: "Please use the teacher dashboard to access performance analytics.",
          actionText: "Go to Teacher Dashboard",
          actionPath: "/teachers/dashboard"
        };
      case 'admin':
        return {
          message: "Please use the admin dashboard to access system features.",
          actionText: "Go to Admin Dashboard",
          actionPath: "/admin/dashboard"
        };
      default:
        return {
          message: "Please contact your administrator for assistance.",
          actionText: "Back to Login",
          actionPath: "/login"
        };
    }
  };

  const recommendation = getRecommendedAction();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            Sorry, you don&apos;t have permission to access this page.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700 mb-2">
            <span className="font-semibold">Required access level:</span>{' '}
            {requiredRoles && requiredRoles.length > 0 
              ? requiredRoles.map(getRoleDisplayName).join(' or ')
              : 'Not specified'
            }
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Your current role:</span>{' '}
            {getRoleDisplayName(user?.role)}
          </p>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">{recommendation.message}</p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate(recommendation.actionPath)}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {recommendation.actionText}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Go Back
          </button>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          You will be redirected automatically in a few seconds...
        </p>
      </div>
    </div>
  );
};

AccessDeniedPage.propTypes = {
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  redirectPath: PropTypes.string,
};

AccessDeniedPage.defaultProps = {
  requiredRoles: [],
  redirectPath: null,
};

export default AccessDeniedPage;
